<template>
  <PageTemplate>
    <BackButton
        :label="$t('general.button.back')"
        :to="{ name: 'coursePrivateLearningCoachingModuleList' }"
        marginBottom="5"/>

    <div class="d-flex justify-content-between">
      <h6 class="element-header">
        {{ $t('course.private_learning_coaching.title') }}
      </h6>
    </div>

    <div class="element-box">
      <h5 class="form-header">
        {{ $t('course.private_learning_coaching.module.title') }}
      </h5>

      <div v-if="isLoaded">
        <div class="col-lg-8 col-md-12 col-sm-12">
          <form @submit.prevent="submitForm" novalidate>
            <div class="row">
              <div class="col-lg-12 col-md-12 col-sm-12">
                <BaseInput v-model="formData.name"
                           :label="$t('general.name')"
                           :required=true
                           :validation="v$.name"
                           :messages="v$.name.required.$message" />
              </div>
              <div class="col-lg-3 col-md-6 col-sm-12">
                <BaseNumber v-model="formData.cost"
                            :label="$t('general.cost')"
                            :required=true
                            :isCurrency=true
                            :decimalPlaces="4"
                            :validation="v$.cost"
                            :messages="v$.cost.required.$message" />
              </div>
              <div class="col-lg-3 col-md-6 col-sm-12">
                <BaseNumber v-model="formData.unitsPerMeeting"
                            :label="$t('general.units_per_meeting')"
                            :required=true
                            :validation="v$.unitsPerMeeting"
                            :messages="v$.unitsPerMeeting.required.$message" />
              </div>
              <div class="col-lg-3 col-md-6 col-sm-12">
                <BaseNumber v-model="formData.units"
                            :label="$t('general.units')"
                            :required=true
                            :validation="v$.units"
                            :messages="v$.units.required.$message" />
              </div>
            </div>
            <div class="form-buttons-w text-center">
              <SaveButton
                  :label="$t('general.button.save')"
                  :loading="isSubmitting"/>
            </div>
          </form>
        </div>
      </div>
    </div>
  </PageTemplate>
</template>

<script setup>
import coursePrivateLearningCoachingModuleService from '@/api/Course/PrivateLearningCoachingModule';
import BaseInput from '@/components/base/BaseInput.vue';
import BaseNumber from '@/components/base/BaseNumber.vue';
import BackButton from '@/components/button/BackButton.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import { ErrorToast, SuccessToast } from '@/components/VueToast';
import router from '@/router';
import { requiredRule } from '@/shared/validators/validationRules';
import useVuelidate from '@vuelidate/core';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import SaveButton from '@/components/button/SaveButton.vue';

const { t }        = useI18n();
const route        = useRoute();
const moduleId     = route.params.id;
const isLoaded     = ref(false);
const isSubmitting = ref(false);

const formData = ref({
  name: '',
  cost: 0,
  units: 0,
  unitsPerMeeting: 0,
});

const rules = ref({
  name: requiredRule(t),
  cost: requiredRule(t),
  units: requiredRule(t),
  unitsPerMeeting: requiredRule(t),
});

const computedRules = computed(() => rules.value);

const v$ = useVuelidate(computedRules, formData);

async function submitForm() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    if (moduleId) {
      await coursePrivateLearningCoachingModuleService.putModule(formData.value, +moduleId);
    } else {
      await coursePrivateLearningCoachingModuleService.postModule(formData.value);
    }

    await router.push({ name: 'coursePrivateLearningCoachingModuleList' });
    isSubmitting.value = false;

    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

async function getModule() {
  try {
    const { data } = await coursePrivateLearningCoachingModuleService.getModule(+moduleId);
    formData.value = data.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

onMounted(async () => {
  if (moduleId) {
    await getModule();
  }
  isLoaded.value = true;
});

</script>

<style scoped>
</style>